import { Button } from "newui/components/Button";
import React, { useEffect, useMemo, useState } from "react";

import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
} from "@chakra-ui/react";
import tw, { styled, css } from "twin.macro";
import { TabHeader } from "components/TabHeader";
import { Input } from "components/RFQ/Input/Input";
import { createRfq } from "actions/RFQ/rfqsAction";
import { navigate } from "@reach/router";
import { getProjectSupplier } from "actions/RFQ/projectsAction";
import { CloseButton } from "components/RFQ/CloseButton";
import SelectTemplate from "components/RFQ/SelectTemplate/SelectTemplate";
import { SupplierListSuffix } from "pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers/SupplierListSuffix";
import { getSupplierListType } from "newui/features/Rfx/SupplierList/supplier-list-types";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";

const DrawerBodyStyled = styled(DrawerBody)(() => [
	tw`p-6 max-h-[calc(100vh - 100px)]`,
]);

const DrawerHeaderStyled = styled(DrawerHeader)(() => [
	tw`text-black py-6 px-0`,
	css`
		.close-text {
			font-weight: 400;
			font-size: 14px;
			cursor: pointer;
		}

		.header-container {
			display: flex;
			justify-content: space-between;
			justify-items: center;
			align-items: center;
		}

		.clear-container {
			display: flex;
			gap: 24px;
			justify-items: center;
			place-items: center;
		}
	`,
]);

const CreateRFQOverview = ({ onClose, isOpen, projectId, ...props }) => {
	const [selectedProject, setSelectedProject] = useState(projectId);
	const [selectedSupplier, setSelectedSupplier] = useState(null);
	const [selectedRFQOptions, setSelectedRFQOptions] = useState(null);
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	const [projects, setProjects] = useState([]);
	const [suppliers, setSuppliers] = useState([]);
	const { clientPaths } = useAuthContext();

	useEffect(() => {
		getProjectSupplier().then((res) => {
			// Add is_company_approved_list property for testing
			const projectsWithApproval = res.map((project) => ({
				...project,
				supplier_list: project.supplier_list.map((supplier) => ({
					...supplier,
				})),
			}));
			setProjects(projectsWithApproval);
		});
	}, []);

	const projectsOptions = useMemo(
		() =>
			projects.map((project) => ({
				value: project?.id,
				label: project?.name,
				is_onboarding: project.is_onboarding,
			})),
		[projects]
	);

	const handleChangeProject = (e) => {
		setSelectedProject(e.value);
		setSupplierListOption(e.value);
	};

	const setSupplierListOption = (projectId) => {
		const suppliersOpt = projects
			.find((project) => project.id === projectId)
			?.supplier_list.map((list) => ({
				value: list.id,
				label: list.name,
				isCompanyApproved: list.is_company_approved_list === 1,
				suffix: <SupplierListSuffix listType={getSupplierListType(list)} />,
			}));

		if (projectId) {
			const optionsWithSkip = [
				{ value: "skip_supplier_list", label: "Skip Supplier List" },
				...(suppliersOpt || []),
			];
			setSuppliers(optionsWithSkip);
		} else {
			setSuppliers(suppliersOpt || []);
		}

		setSelectedSupplier(null);
	};

	useEffect(() => {
		setSupplierListOption(projectId);
		// eslint-disable-next-line
	}, [projectId, projects]);

	// ensure supplier options are populated when modal opens with selected project
	useEffect(() => {
		if (isOpen && selectedProject && projects.length > 0) {
			setSupplierListOption(selectedProject);
		}
		// eslint-disable-next-line
	}, [isOpen, selectedProject, projects]);

	const handleChangeSupplier = (e) => {
		setSelectedSupplier(e);
	};

	const createRFQ = () => {
		const data = {
			project_id: selectedProject,
			template_id: selectedTemplate?.value || "",
		};

		// always include supplier_list_id, use empty string for null so it gets included in FormData
		if (selectedSupplier?.value === "skip_supplier_list") {
			data.supplier_list_id = "";
		} else {
			data.supplier_list_id = selectedSupplier?.value || "";
		}

		createRfq(data)
			.then((res) => {
				if (res) {
					navigate(clientPaths?.createRfx(res.id), {
						state: { rfqData: res },
					});
				}
			})
			.catch((err) => {
				console.log(err);
			});
	};

	return (
		<div>
			<Drawer
				placement={"right"}
				isOpen={isOpen}
				onClose={onClose}
				size="xl"
				{...props}
			>
				<DrawerOverlay onClick={onClose} />
				<DrawerContent>
					<DrawerHeaderStyled borderBottomWidth="1px">
						<div className="px-6">
							<TabHeader
								noDivider={true}
								heading={"SELECT A PROJECT & SUPPLIER"}
								description={
									"Select your preferred project and supplier to begin creating your request for a quote."
								}
							>
								<CloseButton onClose={onClose} />
							</TabHeader>
						</div>
					</DrawerHeaderStyled>
					<DrawerBodyStyled>
						<div className="mb-8">
							<Input
								onChange={handleChangeProject}
								type="select"
								label="Select Project"
								placeholder="Select Project"
								value={selectedProject}
								options={projectsOptions}
								checkIcon={true}
								disabled={projectId}
								testId="select-project-resource"
							/>
						</div>
						<div
							className="mb-8"
							style={{ opacity: selectedProject ? 1 : 0.5 }}
						>
							<Input
								onChange={handleChangeSupplier}
								type="select"
								label="Select Supplier List"
								placeholder="Select Supplier List"
								options={suppliers}
								isDisabled={selectedProject === null}
								isClearable
								checkIcon={true}
								value={selectedSupplier}
								emptyOptionsText="Supplier List"
								testId="select-supplier-list-resource"
							/>
						</div>

						{selectedProject && selectedSupplier && (
							<SelectTemplate
								setSelectedTemplate={setSelectedTemplate}
								selectedRFQOptions={selectedRFQOptions}
								setSelectedRFQOptions={setSelectedRFQOptions}
							/>
						)}

						<div className="mb-8">
							<Button
								width="100%"
								onClick={createRFQ}
								disabled={
									!selectedSupplier || !selectedProject || !selectedRFQOptions
								}
								data-testid="create-rfq-drawer"
							>
								Create RFx
							</Button>
						</div>
						<div className="text-center">
							<p>
								Haven’t created the project yet?
								<a href={clientPaths.rfxProjects.new}> Start here</a>
							</p>
						</div>
					</DrawerBodyStyled>
				</DrawerContent>
			</Drawer>
		</div>
	);
};

export default CreateRFQOverview;
